import entityManager from '../lib/entityManager';
import Action from '../lib/Action'
import { ActionType, ActionMode } from '../lib/Action';
import { PlayerControlled, WantsToAct, HexPosition, IntendsToAct, MoveForward, Turn, Idle, IsTerrain, IsWalkable, TakesTurns, Archetype } from '../components';
import { HexBearing, HexCoords } from '../grid';
import { isAttackPositionProvider, isDisplayProvider } from '../types/Components';
import { GameEvents } from '../lib/GameEvents';
import getLogger from '../lib/logger';
const logger = getLogger('InputSystem');
import _ from 'lodash';

export default class InputSystem {
  constructor(eventEmitter) {
    this.currentMode = ActionMode.STANDARD;
    this.eventEmitter = eventEmitter;
  }

  setActionMode(mode) {
    // Check if mode is a valid ActionMode value
    const validModes = [ActionMode.STANDARD, ActionMode.RANGED_ATTACK, ActionMode.VISUALIZE];
    if (validModes.includes(mode)) {
      this.currentMode = mode;
      // Dismiss examine info when switching modes
      this.eventEmitter.emit(GameEvents.ENEMY_EXAMINE_DISMISSED);
      this.eventEmitter.emit(GameEvents.CLEAR_ENEMY_PATTERN_HIGHLIGHTS);
      this.eventEmitter.emit(GameEvents.ACTION_MODE_CHANGED, mode);
    } else {
      logger.warn(`Attempted to set invalid action mode: ${mode}`);
    }
  }

  getPossiblePlayerActions(mode) {
    const actions = new Map();
    const playerEntities = entityManager.query([PlayerControlled, WantsToAct, HexPosition]);
    if (playerEntities.length === 0) {
      return actions;
    }
    const player = playerEntities[0];

    // Build worldState for components
    const walkableCoords = new Map();
    entityManager.query([IsTerrain, IsWalkable, HexPosition]).forEach(e => {
        const coords = e.getComponent(HexPosition).coords;
        walkableCoords.set(coords.key, coords);
    });

    const allCoords = new Map();
    entityManager.query([IsTerrain, HexPosition]).forEach(e => {
        const coords = e.getComponent(HexPosition).coords;
        allCoords.set(coords.key, coords);
    });

    const occupiedCoords = new Map();
    entityManager.query([TakesTurns, HexPosition]).forEach(e => {
        const coords = e.getComponent(HexPosition).coords;
        occupiedCoords.set(coords.key, e);
    });
    const worldState = { walkableCoords, occupiedCoords, allCoords };

    // Iterate through components to gather actions for the given mode
    for (const component of Object.values(player.doc)) {
        if (component && typeof component.getPossiblePlayerActions === 'function') {
            const componentActions = component.getPossiblePlayerActions(player, mode, worldState);
            for (const [key, action] of componentActions.entries()) {
                actions.set(key, action);
            }
        }
    }

    return actions;
  }

  initializePlayerTurn() {
    const player = entityManager.query([PlayerControlled, WantsToAct])[0];
    if (!player) return;

    const availableModes = this.getAvailableActionModes();
    const modeStates = this.getModeStates();
    const activeModes = Object.keys(modeStates).filter(mode => modeStates[mode]);

    // Handle no-action state
    if (activeModes.length === 0) {
      logger.log('No actions available for player, forcing intent to IDLE.');
      player.addComponent(IntendsToAct, new IntendsToAct({ actionType: ActionType.IDLE, details: {} }))
      player.removeComponent(WantsToAct);
      this.eventEmitter.emit(GameEvents.PLAYER_ACTION_TAKEN);
      return;
    }

    // Handle invalid current mode
    if (!modeStates[this.currentMode]) {
      this.setActionMode(activeModes[0] || ActionMode.STANDARD);
    }

    this.eventEmitter.emit(GameEvents.ACTION_MODES_UPDATED, { availableModes, modeStates });
  }

  getAvailableActionModes() {
    const player = entityManager.query([PlayerControlled])[0];
    if (!player) return [ActionMode.VISUALIZE];

    const modes = new Set();
    modes.add(ActionMode.VISUALIZE); // Always available

    for (const component of Object.values(player.doc)) {
        if (component && component.constructor && component.constructor.supportedModes) {
            component.constructor.supportedModes.forEach(mode => modes.add(mode));
        }
    }
    return Array.from(modes);
  }

  getModeStates() {
    const states = {};
    const availableModes = this.getAvailableActionModes();
    if (availableModes.length === 0) return states;

    for (const mode of availableModes) {
      if (mode === ActionMode.VISUALIZE) {
        states[mode] = true; // Always enabled
      } else {
        const actions = this.getPossiblePlayerActions(mode);
        states[mode] = actions.size > 0;
      }
    }
    return states;
  }

  getUniquePlayerActions() {
    const allActions = [];
    const availableModes = this.getAvailableActionModes();

    for (const mode of availableModes) {
        const modeActions = this.getPossiblePlayerActions(mode);
        allActions.push(...modeActions.values());
    }

    const uniquePlayerActions = _.uniqWith(allActions, (a, b) => {
      return Action.isEquivalent(a, b);
    });

    return uniquePlayerActions;
  }

  applyActionToEntity(entity, action) {
    entity.addComponent(IntendsToAct, new IntendsToAct({ actionType: action.type, details: action.details }));
  }

  submitPlayerAction(action) {
    const player = entityManager.query([PlayerControlled, WantsToAct])[0];
    if (!player) return;

    this.applyActionToEntity(player, action);

    player.removeComponent(WantsToAct);
  }

  handleTap(hexCoords) {
    const hexKey = HexCoords.get(hexCoords).key;

    // Check if clicking terrain (for dismissing pattern)
    const walkableCoords = new Map();
    entityManager.query([IsTerrain, IsWalkable, HexPosition]).forEach(e => {
      const coords = e.getComponent(HexPosition).coords;
      walkableCoords.set(coords.key, coords);
    });

    const occupiedCoords = new Map();
    entityManager.query([TakesTurns, HexPosition]).forEach(e => {
      const coords = e.getComponent(HexPosition).coords;
      occupiedCoords.set(coords.key, e);
    });

    const isTerrainClick = walkableCoords.has(hexKey) && !occupiedCoords.has(hexKey);
    if (isTerrainClick) {
      this.eventEmitter.emit(GameEvents.CLEAR_ENEMY_PATTERN_HIGHLIGHTS);
      this.eventEmitter.emit(GameEvents.ENEMY_EXAMINE_DISMISSED);
    }

    // In VISUALIZE mode, never submit actions
    if (this.currentMode === ActionMode.VISUALIZE) {
      this.handleEnemyClick(hexCoords, occupiedCoords);
      return;
    }

    const possibleActions = this.getPossiblePlayerActions(this.currentMode);
    const action = possibleActions.get(hexKey);

    if (action) {
      this.eventEmitter.emit(GameEvents.PLAYER_ACTION_REQUESTED, action)
    }
  }

  handleEnemyClick(hexCoords, occupiedCoords) {
    const hexKey = HexCoords.get(hexCoords).key;

    // Lazy-build occupiedCoords if not provided
    if (!occupiedCoords) {
      occupiedCoords = new Map();
      entityManager.query([TakesTurns, HexPosition]).forEach(e => {
        const coords = e.getComponent(HexPosition).coords;
        occupiedCoords.set(coords.key, e);
      });
    }

    // Check if any actor is at this hex
    const entityAtHex = occupiedCoords.get(hexKey);
    if (!entityAtHex) return;

    // Collect attack patterns from all components (union)
    const attackHexes = new Set();
    for (const component of Object.values(entityAtHex.doc)) {
      if (isAttackPositionProvider(component)) {
        const pattern = component.getAttackPattern(entityAtHex);
        pattern.forEach(hex => attackHexes.add(hex.key));
      }
    }

    if (attackHexes.size > 0) {
      // Filter to walkable tiles only
      const walkableCoords = new Map();
      entityManager.query([IsTerrain, IsWalkable, HexPosition]).forEach(e => {
        const coords = e.getComponent(HexPosition).coords;
        walkableCoords.set(coords.key, coords);
      });

      const visiblePattern = Array.from(attackHexes)
        .filter(key => walkableCoords.has(key))
        .map(key => {
          const [q, r] = key.split('.').map(Number);
          return HexCoords.get(q, r);
        });

      if (visiblePattern.length > 0) {
        const hexCoords = visiblePattern.map(c => ({ q: c.q, r: c.r }));
        this.eventEmitter.emit(GameEvents.UPDATE_ENEMY_PATTERN_HIGHLIGHTS, {
          hexCoords,
          enemyId: entityAtHex.id
        });
      }
    }

    // Collect display info from DisplayProvider components
    const displayInfos = [];
    for (const component of Object.values(entityAtHex.doc)) {
      if (isDisplayProvider(component)) {
        displayInfos.push(component.getDisplayInfo(entityAtHex));
      }
    }
    if (displayInfos.length === 0) return;

    const archetype = entityAtHex.getComponent(Archetype);
    const entityName = archetype?.displayName ?? archetype?.name ?? 'Unknown';
    this.eventEmitter.emit(GameEvents.ENEMY_EXAMINED, {
      entityId: entityAtHex.id,
      enemyName: entityName,
      displayInfos,
    });
  }
}
